import { TransportIcon } from '@app/ccf/components/transport-icon';
import {
  nodeTypeOptions,
  storageManagedByOptions,
} from '@app/ccf/options/node-options';
import { storageDurationOptions } from '@app/ccf/options/storage-duration-options';
import { transportManagedByOptions } from '@app/ccf/options/transport-options';
import { useGetTransportModes } from '@shared/api/hooks/ccf/transport-modes';
import {
  StorageManagedByEnum,
  TransportManagedByEnum,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import { RepeatableRowComponentProps } from '@shared/components/form/types';
import { Button } from '@shared/components/ui/button';
import { Skeleton } from '@shared/components/ui/skeleton';
import { cn } from '@shared/lib/utils';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFormattedTransportModes } from '../../utils';

interface DistributionFlowRowProps extends RepeatableRowComponentProps {}

export default function DistributionFlowRow({
  index,
  remove,
  insert,
}: DistributionFlowRowProps) {
  const { getValues, setValue, watch } = useFormContext();

  const length = getValues('segments').length;

  const { data: transportModes, isLoading: transportModesLoading } =
    useGetTransportModes({
      params: {},
    });

  const transportModeOptions = useFormattedTransportModes(
    transportModes?.results || []
  );

  const isFirstRow = index === 0;

  const thisRowNodeType = watch(`segments.${index}.nodeType`);
  const thisRowTransportMode = watch(`segments.${index}.transportMode`);
  const thisRowStorageManagedBy = watch(`segments.${index}.storageManagedBy`);
  const thisRowTransportManagedBy = watch(
    `segments.${index}.transportManagedBy`
  );

  const previousRowStorageManagedBy = watch(
    `segments.${index - 1}.storageManagedBy`
  );
  const previousRowTransportManagedBy = watch(
    `segments.${index - 1}.transportManagedBy`
  );

  const inCompanyStorage = useMemo(
    () =>
      thisRowStorageManagedBy === StorageManagedByEnum['company'] ||
      thisRowStorageManagedBy === StorageManagedByEnum['third_party'],
    [thisRowStorageManagedBy]
  );

  const inClientStorage = useMemo(
    () => thisRowStorageManagedBy === StorageManagedByEnum['client'],
    [thisRowStorageManagedBy]
  );

  const isDownstreamRow = useMemo(
    () =>
      previousRowStorageManagedBy === StorageManagedByEnum['client'] ||
      previousRowTransportManagedBy === TransportManagedByEnum['client'] ||
      previousRowStorageManagedBy === StorageManagedByEnum['downstream'] ||
      previousRowTransportManagedBy === TransportManagedByEnum['downstream'],
    [previousRowStorageManagedBy, previousRowTransportManagedBy]
  );

  const getRowIcon = () => {
    const icon = nodeTypeOptions.find(
      (option) => option.value === thisRowNodeType
    )?.icon;

    return icon || <Icons.warehouse className="size-6 text-primary-600" />;
  };

  const setDownstreamRows = (index: number) => {
    const segments = getValues(`segments`);

    for (let i = index + 1; i < segments.length; i++) {
      setValue(
        `segments.${i}.storageManagedBy`,
        StorageManagedByEnum['downstream']
      );
      setValue(
        `segments.${i}.transportManagedBy`,
        TransportManagedByEnum['downstream']
      );
    }
  };

  const unsetDownstreamRows = (index: number) => {
    const segments = getValues(`segments`);
    for (let i = index + 1; i < segments.length; i++) {
      setValue(`segments.${i}.storageManagedBy`, '');
      setValue(`segments.${i}.transportManagedBy`, '');
    }
  };

  const unsetDownstreamRowsManagedBy = (index: number) => {
    const segments = getValues(`segments`);
    for (let i = index + 1; i < segments.length; i++) {
      setValue(`segments.${i}.transportManagedBy`, '');
      setValue(`segments.${i}.storageManagedBy`, '');
    }
  };

  const handleStorageSelect = (value: string) => {
    // When client storage is selected, set transport to downstream and cascade
    if (value === StorageManagedByEnum['client']) {
      setValue(
        `segments.${index}.transportManagedBy`,
        TransportManagedByEnum['downstream']
      );
      setDownstreamRows(index);
      return;
    }

    // If changing from client storage to something else, clear downstream cascade
    if (
      thisRowStorageManagedBy === StorageManagedByEnum['client'] &&
      value !== StorageManagedByEnum['client']
    ) {
      setValue(`segments.${index}.transportManagedBy`, '');
      unsetDownstreamRows(index);
      return;
    }
  };

  const handleTransportSelect = (value: string) => {
    // When client transport is selected, cascade downstream
    if (value === TransportManagedByEnum['client']) {
      setDownstreamRows(index);
      return;
    }

    // If changing from client transport, clear downstream cascade
    if (thisRowTransportManagedBy === TransportManagedByEnum['client']) {
      unsetDownstreamRows(index);
      return;
    }
  };

  const insertRow = () => {
    const isDownstream =
      thisRowStorageManagedBy === StorageManagedByEnum['downstream'] ||
      thisRowTransportManagedBy === TransportManagedByEnum['downstream'];

    insert(index + 1, {
      storageManagedBy: isDownstream ? StorageManagedByEnum['downstream'] : '',
      nodeType: '',
      transportMode: '',
      distance: '',
      transportManagedBy: isDownstream
        ? TransportManagedByEnum['downstream']
        : '',
      storageDuration: '',
    });
  };

  const removeRow = () => {
    setValue(`segments.${index}.storageDuration`, '');

    if (!isDownstreamRow) {
      unsetDownstreamRowsManagedBy(index);
    }

    remove(index);
  };

  const filteredStorageManagedByOptions = useMemo(() => {
    if (isFirstRow) {
      return storageManagedByOptions.filter(
        (option) =>
          option.value !== StorageManagedByEnum['client'] &&
          option.value !== StorageManagedByEnum['downstream'] &&
          option.value !== StorageManagedByEnum['supplier']
      );
    }

    if (thisRowStorageManagedBy !== StorageManagedByEnum['downstream']) {
      return storageManagedByOptions.filter(
        (option) => option.value !== StorageManagedByEnum['supplier']
      );
    }

    return storageManagedByOptions;
  }, [isFirstRow, thisRowStorageManagedBy]);

  const filteredTransportManagedByOptions = useMemo(() => {
    if (thisRowTransportManagedBy !== TransportManagedByEnum['downstream']) {
      return transportManagedByOptions.filter(
        (option) => option.value !== TransportManagedByEnum['supplier']
      );
    }

    return transportManagedByOptions.filter(
      (option) => option.value !== TransportManagedByEnum['supplier']
    );
  }, [thisRowTransportManagedBy]);

  if (transportModesLoading) {
    return <Skeleton className="h-12 w-full rounded-md bg-gray-100" />;
  }

  return (
    <div className="flex space-y-2">
      <div className="max-w-3xl space-y-2">
        <div
          className={cn(
            'flex w-[700px] items-center space-x-3 rounded-md border-2 border-primary-300 bg-gray-00 px-4 py-2',
            {
              'border-primary-600': inCompanyStorage,
            }
          )}
        >
          <div
            className={cn('', {
              'opacity-60': isDownstreamRow,
            })}
          >
            {getRowIcon()}
          </div>
          <div className="flex w-full space-x-2">
            <SelectFormField
              name={`segments.${index}.nodeType`}
              options={nodeTypeOptions}
              placeholder="Site"
              className="w-40"
            />
            <SelectFormField
              name={`segments.${index}.storageDuration`}
              options={storageDurationOptions}
              placeholder="Storage"
              className="w-40"
            />
            <div className="flex flex-1 justify-end">
              <SelectFormField
                name={`segments.${index}.storageManagedBy`}
                options={filteredStorageManagedByOptions}
                className="w-44"
                disabled={isDownstreamRow}
                handleSelect={handleStorageSelect}
              />
            </div>
          </div>
        </div>

        <div className="flex w-full items-center space-x-2 px-4 pl-3">
          <div
            className={cn('mr-1 flex items-center space-x-2', {
              'opacity-60': isDownstreamRow,
            })}
          >
            <Icons.arrowDown className="size-8 text-primary-600" />
            <TransportIcon
              transportModeId={thisRowTransportMode}
              transportModeOptions={transportModeOptions}
            />
          </div>
          <SelectFormField
            name={`segments.${index}.transportMode`}
            options={transportModeOptions}
            className="w-32 shrink-0"
            placeholder="Transport"
          />
          <InputFormField
            name={`segments.${index}.distance`}
            className="w-40 shrink-0"
            inputClassName="text-right"
            placeholder="Distance"
            adornmentRight="km"
            type="number"
            min={1}
          />
          <div className="flex flex-1 justify-end">
            <SelectFormField
              name={`segments.${index}.transportManagedBy`}
              options={filteredTransportManagedByOptions}
              className="w-44"
              disabled={inClientStorage || isDownstreamRow}
              handleSelect={handleTransportSelect}
            />
          </div>
        </div>
      </div>
      {(!isFirstRow || length < 2) && (
        <div className="flex text-primary-400">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn('size-12 -mt-0.5')}
            onClick={insertRow}
          >
            <Icons.plus className="size-6" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn('size-12 -mt-0.5')}
            disabled={length === 1}
            onClick={removeRow}
          >
            <Icons.x className="size-6" />
          </Button>
        </div>
      )}
    </div>
  );
}
